import React, { useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import * as Yup from 'yup';
import { Loader, MessageBlock, FormInput, Button, FormImage, FormLabel, FormSelect } from '@getro/rombo';
import { openChat } from 'helpers/supportChat';
import { networkTypes } from 'helpers/networkTypes';
import strings from '../../../../strings';
import { GenericError } from '../../../../components/molecules/genericError';

const formSchema = Yup.object().shape({
  logoUrl: Yup.string().label('Logo URL').url().nullable(),
});

const General = ({ activeNetwork, generalSettings, fetchError, saveError, submitting, onSubmit }) => {
  const formRef = useRef(null);

  const handleSubmit = ({ kind, ...values }) => {
    onSubmit({ ...values, kind: kind.value });
  };

  if (fetchError) {
    return <MessageBlock>{strings.genericError}</MessageBlock>;
  }

  if (generalSettings === null) {
    return <Loader />;
  }

  const type = networkTypes.find((item) => item.id === activeNetwork.kind);

  return (
    <>
      <Box pb="32px">
        <Text fontSize="14px" fontWeight="medium" color="text.dark">
          Name
        </Text>
        <Text fontSize="14px" mt="4px" pb="8px">
          Your network name is used on your team dashboard, invitation emails, shared lists, and your job board. To
          update it,{' '}
          <Box
            onClick={() => openChat()}
            as="span"
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            contact us
          </Box>
          .
        </Text>
        <FormInput value={activeNetwork.name} disabled />
      </Box>
      <Formik
        innerRef={formRef}
        initialValues={{
          ...generalSettings,
          kind: type
            ? {
                label: type.name,
                value: type.id,
                description: type.description,
              }
            : undefined,
        }}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, status, setFieldValue }) => (
          <Form>
            <Flex alignItems="center">
              <Field
                width="64px"
                height="64px"
                label="Logo"
                filestackApiKey={import.meta.env.VITE_FILESTACK_API_KEY}
                name="logoUrl"
                component={FormImage}
                field={{ name: 'logoUrl', value: values.logoUrl }}
                helpText="Your network logo is used on your team dashboard, invitation emails, shared lists, and your job board. Upload a 256x256px, .png, .gif, or .jpg file."
                form={{
                  setFieldValue,
                  name: 'logoUrl',
                }}
              />
            </Flex>
            <Flex sx={{ gap: '8px', flexDirection: 'column', mt: '32px' }}>
              <Box>
                <FormLabel>Network type</FormLabel>
                <Text fontSize="1" lineHeight="20px">
                  Choose what best describes your network. We’ll tailor your experience to help you unlock the right
                  intros.
                </Text>
              </Box>
              <Field
                name="kind"
                component={FormSelect}
                options={networkTypes.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                value={values.kind}
              />
            </Flex>
            {!!(status?.error || saveError) && <GenericError />}
            <Box mt="32px">
              <Button size="small" type="submit" loading={submitting}>
                Save
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

General.propTypes = {
  activeNetwork: PropTypes.object.isRequired,
  fetchError: PropTypes.string,
  generalSettings: PropTypes.shape({
    logoUrl: PropTypes.string,
  }),
  saveError: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

General.defaultProps = {
  fetchError: null,
  generalSettings: null,
};

export default General;
